import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { addDays, format, isSameDay, startOfDay } from 'date-fns';
import * as maptilersdk from '@maptiler/sdk';

import MapComponent from './components/Map/map';
import AppLogo from './components/Header/Headers';
import ConditionalSignUpComponent from './components/SignUpPicker';
import CityPicker from './components/Context/CityPicker';
import SignUpModal from './components/Waitlist/SignUpModal';
import { ToastContainer, useToast } from './components/Context/Toast';
import BottomSheet from './components/Filters/BottomSheet';
import GeolocateButton from './components/Map/GeolocateButton';

import { AuthProvider } from './contexts/AuthContext';
import { auth } from './lib/firebase';
import {
  Vendor,
  DateRange,
  MapBounds,
  CacheEntry,
  CustomList,
  CityPreset,
  FilterPreset,
  ListItem,
  ActiveListItem,
  VendorDelta,
} from './types/types';
import { BottomSheetHandle } from './components/Filters/BottomSheet';
import { logger } from './lib/logger';
import { isMobileDevice } from './lib/deviceDetector';
import { INITIAL_DATE, APP_CONFIG, DEFAULT_CATEGORIES, LAST_CITY_KEY } from './lib/appConstants';
import { deduplicateVendors, filterVendorsByDate, mergeVendorUpdates } from './lib/vendorUtils';
import { useVendorSelection } from './hooks/useVendorSelection';
import { useGPSUpdates } from './hooks/useGPSUpdates';
import { useFetchVendors } from './hooks/useFetchVendors';
import { useVendorPolling } from './hooks/useVendorPolling';

import './styles/variables.css';
import './styles/app-ui.css';
import './styles/mobile.css';

import { getDatesWithVendors } from './lib/dateUtils';
import { DEFAULT_ZOOM } from './lib/mapUtils';
import { shouldIgnoreBoundsChange } from './lib/enhancedMarkerTouch';

import {
  generateShareUrl,
  copyToClipboard,
  parseShareLinkFromUrl,
  clearShareLinkParams,
  hasShareLinkParams,
  parseListFromUrl,
  generateListShareUrl,
  clearListParamIfMatches,
} from './lib/shareUtils';
import {
  getSharedList,
  createList,
  addItemToList,
  removeItemFromList,
  getUserLists,
  updateList,
  getList,
  deleteList,
} from './lib/listApi';
import ExitShareViewButton from './components/Filters/ExitShareViewButton';
import { CuratedListMeta } from './lib/vendorApi';
import { createPortal } from 'react-dom';
import EventSubmitModal from './components/Events/EventSubmitModal';
import { getUserEvents, deleteUserEvent, submitEventForReview } from './lib/eventApi';
import { getCalendarEvents, getPinnedEvents } from './lib/calendarApi';
import { UserEvent, ExpandedVendor, GCalEvent, PinnedEvent } from './types/types';

const EMPTY_VENDORS_ARRAY: Vendor[] = [];
const EMPTY_EXPANDED_VENDORS: ExpandedVendor[] = [];

const DEFAULT_PRESETS: FilterPreset[] = [
  { id: 'slot-1', type: 'category', value: 'active', active: false },
  { id: 'slot-2', type: 'category', value: 'music', active: false },
  { id: 'slot-3', type: 'category', value: 'event+pop-up', active: false },
];

const App: React.FC = () => {
  const [vendorMap, setVendorMap] = useState<Map<string, Vendor>>(new Map());
  const [_categories, setCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [visibleVendorIds, setVisibleVendorIds] = useState<number[]>([]);
  const [isMobile, setIsMobile] = useState(() => isMobileDevice());
  const [apiCache, setApiCache] = useState(() => new Map<string, CacheEntry>());
  const [hasValidData, setHasValidData] = useState(false);
  const [apiDateRange, setApiDateRange] = useState<DateRange>(() => {
    if (hasShareLinkParams()) {
      const shareLink = parseShareLinkFromUrl();
      if (shareLink.date) {
        const shareDate = startOfDay(shareLink.date);
        logger.log('Initializing apiDateRange with share link date:', shareDate);
        return {
          startDate: shareDate,
          endDate: shareDate,
        };
      }
    }
    return {
      startDate: INITIAL_DATE,
      endDate: addDays(INITIAL_DATE, APP_CONFIG.MAX_API_DAYS_RANGE),
    };
  });
  const [mapDateFilter, setMapDateFilter] = useState<Date>(() => {
    if (hasShareLinkParams()) {
      const shareLink = parseShareLinkFromUrl();
      if (shareLink.date) {
        logger.log('Initializing mapDateFilter with share link date:', shareLink.date);
        return startOfDay(shareLink.date);
      }
    }
    return INITIAL_DATE;
  });
  const [locationState, setLocationState] = useState<
    'pending' | 'granted' | 'denied' | 'unavailable'
  >(() => {
    try {
      const stored = localStorage.getItem(LAST_CITY_KEY);
      return stored ? 'denied' : 'pending';
    } catch {
      return 'pending';
    }
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedCity, setSelectedCity] = useState<CityPreset | null>(() => {
    try {
      if (process.env.NODE_ENV === 'development' && window.location.search.includes('reset')) {
        localStorage.removeItem(LAST_CITY_KEY);
        return null;
      }
      const stored = localStorage.getItem(LAST_CITY_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [signUpModalState, setSignUpModalState] = useState<{ isOpen: boolean; headerText: string }>(
    {
      isOpen: false,
      headerText: '',
    }
  );
  const [pendingDeepLink, setPendingDeepLink] = useState<{
    eventId: number;
    date: Date;
  } | null>(null);
  const [shareViewState, setShareViewState] = useState<{
    active: boolean;
    eventId: number;
    date: Date;
    coordinates: [number, number];
    isPast: boolean;
    isFuture: boolean;
  } | null>(null);
  const [_currentZoom, setCurrentZoom] = useState<number>(DEFAULT_ZOOM);
  // Add these new state declarations:
  const [curatedLists, setCuratedLists] = useState<CuratedListMeta[]>([]);
  const [filterPresets, setFilterPresets] = useState<FilterPreset[]>(DEFAULT_PRESETS);
  const [activeListVendorIds, setActiveListVendorIds] = useState<number[]>([]);
  const [cityDrawerState, setCityDrawerState] = useState<{ open: boolean; contextText?: string }>({
    open: false,
  });
  const [userLists, setUserLists] = useState<CustomList[]>([]);
  const [listItemsCache, setListItemsCache] = useState<Record<number, ActiveListItem[]>>({});
  const [curatedItemsCache, setCuratedItemsCache] = useState<Record<string, ActiveListItem[]>>({});
  const [savedIndicatorListId, setSavedIndicatorListId] = useState<number | null>(null);
  const [activeListId, setActiveListId] = useState<number | null>(null);
  const [eventSubmitState, setEventSubmitState] = useState<{
    isOpen: boolean;
    coordinates: [number, number] | null;
  }>({ isOpen: false, coordinates: null });
  const [isDiscardingPreview, setIsDiscardingPreview] = useState(false);
  const [editingEvent, setEditingEvent] = useState<UserEvent | null>(null);
  const [userEvents, setUserEvents] = useState<UserEvent[]>([]);
  const [pinnedEvents, setPinnedEvents] = useState<PinnedEvent[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<GCalEvent[]>([]);
  const [calendarEventsLoading, setCalendarEventsLoading] = useState(false);
  const [calendarEventsError, setCalendarEventsError] = useState<string | null>(null);

  const bottomSheetRef = useRef<BottomSheetHandle>(null);
  // Ref tracks the active list ID synchronously to avoid race conditions
  // when two pins happen before a state update re-renders
  const activeListIdRef = useRef<number | null>(null);
  const listCreationPromiseRef = useRef<Promise<number> | null>(null);

  const activeCategories = useMemo(
    () =>
      filterPresets
        .filter(p => p.active && p.type === 'category')
        .flatMap(p => (p.value === 'event+pop-up' ? ['event', 'pop-up'] : [p.value])),
    [filterPresets]
  );
  const activeListUuid = useMemo(
    () => filterPresets.find(p => p.active && p.type === 'list')?.value ?? null,
    [filterPresets]
  );

  // Derived from cache — used by map/panel/header for pin state
  const activeListItems = useMemo(
    () => (activeListId !== null ? (listItemsCache[activeListId] ?? []) : []),
    [activeListId, listItemsCache]
  );

  const hasInitialized = useRef(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const lastMapBoundsRef = useRef<MapBounds | null>(null);
  const lastServerTimeRef = useRef<string | null>(null);
  const mapRef = useRef<maptilersdk.Map | null>(null);

  const { toasts, showToast, dismissToast } = useToast();

  // Process share link on mount
  useEffect(() => {
    if (hasShareLinkParams()) {
      const shareLink = parseShareLinkFromUrl();

      if (shareLink.eventId && shareLink.date && shareLink.coordinates) {
        const today = startOfDay(new Date());
        const shareDate = startOfDay(shareLink.date);

        const isPast = shareDate < today;
        const isFuture = shareDate > today;

        logger.log('Share link detected:', {
          eventId: shareLink.eventId,
          date: shareLink.date,
          coordinates: shareLink.coordinates,
          isPast,
          isFuture,
        });

        // Set the date filter FIRST, before anything else
        setMapDateFilter(shareLink.date);

        // Set API date range to ONLY the shared date
        setApiDateRange({
          startDate: shareLink.date,
          endDate: shareLink.date,
        });

        // Set share view state
        setShareViewState({
          active: true,
          eventId: shareLink.eventId,
          date: shareLink.date,
          coordinates: shareLink.coordinates,
          isPast,
          isFuture,
        });

        // Set location state to 'unavailable' to prevent permission popup
        setLocationState('unavailable');

        // Clear URL params
        clearShareLinkParams();
      }
    }
  }, []); // Run once on mount

  useEffect(() => {
    let didRun = false;
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if (didRun || !user) return; // only once, only when logged in
      didRun = true;
      try {
        const lists = await getUserLists();
        setUserLists(lists);
        if (lists.length === 0) return;
        const firstList = lists[0];
        activeListIdRef.current = firstList.id;
        setActiveListId(firstList.id);
        const detail = await getList(firstList.id);
        if (detail.items && detail.items.length > 0) {
          // CAUTION: this map runs inside the outer try/catch. Any throw here silently
          // prevents getUserEvents() below from being called. Use optional chaining on
          // all nested fields — a null/undefined schedule from the backend will otherwise
          // abort the entire bootstrap and leave userEvents empty with no visible error.
          const items: ActiveListItem[] = detail.items.map(item => ({
            id: item.id,
            vendor_id: item.vendor.id,
            event_date: item.event_date,
            vendor_name: item.vendor.name,
            vendor_category: item.vendor.category,
            vendor_expiry: item.vendor.expiry,
            vendor_schedule_start_time: item.vendor.schedule?.start_time ?? '',
            vendor_schedule_end_time: item.vendor.schedule?.end_time ?? '',
            vendor_schedule: item.vendor.schedule ?? null,
          }));
          setListItemsCache({ [firstList.id]: items });
        }
        getUserEvents()
          .then(setUserEvents)
          .catch(() => {});
        getPinnedEvents()
          .then(setPinnedEvents)
          .catch(() => {});
      } catch {
        // Network error — silently ignore
      }
    });
    return unsubscribe;
  }, []);

  const handleMapRef = useCallback((map: maptilersdk.Map | null) => {
    mapRef.current = map;
  }, []);

  // Derive vendors array from accumulated map for all downstream consumers
  const vendors = useMemo(() => Array.from(vendorMap.values()), [vendorMap]);

  // Merge incoming vendors (from full fetch) into the accumulated map
  const handleVendorsLoaded = useCallback((newVendors: Vendor[]) => {
    setVendorMap(prev => {
      const next = new Map(prev);
      newVendors.forEach(v => {
        if (v.displayID) next.set(v.displayID, v);
      });
      return next;
    });
  }, []);

  // Apply delta from polling into the accumulated map
  const handleDelta = useCallback((delta: VendorDelta) => {
    setVendorMap(prev => mergeVendorUpdates(prev, delta));
  }, []);

  // Update a single vendor's GPS location in the accumulated map
  const updateVendorLocation = useCallback(
    (vendorId: number, newLocation: { latitude: number; longitude: number }) => {
      setVendorMap(prev => {
        const next = new Map(prev);
        for (const [key, v] of next) {
          if (v.id === vendorId) {
            next.set(key, { ...v, coordinates: [newLocation.longitude, newLocation.latitude] });
          }
        }
        return next;
      });
    },
    []
  );

  // hooks
  // set API date range and sync map date filter
  const { selection, handleVendorSelect, resetSelection } = useVendorSelection(
    vendors,
    APP_CONFIG.MAX_API_DAYS_RANGE
  );
  const { updateGPSLocations } = useGPSUpdates(
    vendors,
    visibleVendorIds,
    selection.vendorId,
    updateVendorLocation
  );

  const { fetchVendors } = useFetchVendors({
    locationState,
    mapBounds,
    apiDateRange,
    apiCache,
    setApiCache,
    onVendors: handleVendorsLoaded,
    setCategories,
    setIsLoading,
    setIsInitialLoad,
    isInitialLoad,
    setCuratedLists,
    onFetchSuccess: ts => {
      lastServerTimeRef.current = ts;
    },
  });

  useVendorPolling({
    mapBounds,
    apiDateRange,
    isEnabled: !isInitialLoad,
    onDelta: handleDelta,
    lastServerTimeRef,
  });

  // Handle share view initialization - separate from normal location flow
  useEffect(() => {
    if (shareViewState?.active && !hasInitialized.current && mapBounds) {
      logger.log('Share view: Initializing with share location');
      hasInitialized.current = true;

      // Fetch vendors for the share view location
      fetchVendors({ immediate: true });
    }
  }, [shareViewState, mapBounds, fetchVendors]);

  // Auto-select shared event after vendors are loaded
  useEffect(() => {
    if (shareViewState?.active && vendors.length > 0 && !selection.vendorId) {
      // Find the shared event in the loaded vendors
      const sharedVendor = vendors.find(v => v.id === shareViewState.eventId);

      if (sharedVendor) {
        const displayId = `${sharedVendor.id}-${format(shareViewState.date, 'yyyy-MM-dd')}`;

        logger.log('Auto-selecting shared event:', {
          vendorId: sharedVendor.id,
          displayId,
          vendorName: sharedVendor.name,
        });

        // Select the event in the panel
        handleVendorSelect(sharedVendor.id, displayId);

        // Use handlePanelCardClick to also open the popup
        if (sharedVendor.coordinates) {
          setTimeout(() => {
            if ((window as any).handlePanelCardClick) {
              (window as any).handlePanelCardClick(
                sharedVendor.id,
                displayId,
                sharedVendor.coordinates,
                false // shouldBeSelected = false to not expand a shared link by default
              );
            }
          }, 500);
        }
      } else {
        logger.warn('Shared event not found in loaded vendors:', {
          searchingFor: shareViewState.eventId,
          loadedVendors: vendors.map(v => v.id),
        });
      }
    }
  }, [shareViewState, vendors, selection.vendorId, handleVendorSelect, isMobile]);

  const handleZoomChange = useCallback((zoom: number) => {
    setCurrentZoom(zoom);
  }, []);

  const handleMapDateChange = useCallback((date: Date) => {
    setMapDateFilter(prevDate => {
      // Only update if the date actually changed
      if (prevDate && date.getTime() === prevDate.getTime()) {
        return prevDate; // Return same reference to prevent re-renders
      }
      return date;
    });
    setHasValidData(true);
  }, []);

  // Handler for visible vendors from the map component
  const handleVisibleVendorsChange = useCallback((vendorIds: number[]) => {
    logger.log('App received visible vendors:', vendorIds);
    setVisibleVendorIds(vendorIds);
  }, []);

  // Fetch vendors with hybrid declarative/imperative pattern
  useEffect(() => {
    // === INITIAL LOAD: Setup one-time event listener ===
    if (locationState === 'granted' && !hasInitialized.current) {
      if (!mapBounds) {
        return; // Wait for initial bounds to be set
      }

      const handleInitialMoveComplete = (event: Event) => {
        const customEvent = event as CustomEvent<{ bounds: MapBounds }>;
        const eventBounds = customEvent.detail.bounds;
        logger.debug('App: Initial map move complete, received bounds:', eventBounds);

        // Update bounds state - this will trigger the fetch via the effect below
        setMapBounds(eventBounds);
        hasInitialized.current = true;
      };

      window.addEventListener('initialMapMoveComplete', handleInitialMoveComplete);
      return () => window.removeEventListener('initialMapMoveComplete', handleInitialMoveComplete);
    }

    // === INITIAL LOAD: Handle denied/unavailable (no map animation) ===
    if (
      (locationState === 'denied' || locationState === 'unavailable') &&
      !hasInitialized.current
    ) {
      if (!mapBounds) {
        return; // Wait for default bounds
      }
      hasInitialized.current = true;
      logger.debug('App: Location denied/unavailable, fetching with default bounds');
      fetchVendors({ immediate: true }); // Skip debounce on initial load
      return;
    }

    // === NORMAL OPERATION: Declarative fetch on bounds change ===
    // This handles: viewport changes, pending bounds from refresh button, initial load completion
    if (hasInitialized.current && mapBounds) {
      logger.debug('App: Map bounds changed, triggering fetch');
      fetchVendors(); // Uses cache/significance checks
    }
  }, [locationState, fetchVendors, mapBounds]);

  // Clear accumulated vendor store when date range changes (different date = different data set)
  useEffect(() => {
    setVendorMap(new Map());
  }, [apiDateRange]);

  // Handle ?list= URL param on mount
  const pendingListFlyToRef = useRef<ListItem[] | null>(null);
  const listShareProcessed = useRef(false);

  useEffect(() => {
    const listUuid = parseListFromUrl();
    if (!listUuid || listShareProcessed.current) return;
    listShareProcessed.current = true;

    const loadSharedList = async () => {
      try {
        const list = await getSharedList(listUuid);
        handleAddList(list);

        if (list.items && list.items.length > 0) {
          pendingListFlyToRef.current = list.items;
        }
      } catch (err) {
        logger.error('Failed to load shared list from URL:', err);
        showToast('Could not load this list', 'error');
      }
    };

    loadSharedList();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fly to soonest event once vendors are loaded
  useEffect(() => {
    if (!pendingListFlyToRef.current || isInitialLoad || !mapRef.current) return;

    const items = pendingListFlyToRef.current;
    const today = startOfDay(new Date());

    // Find items with future or today dates, sorted ascending
    const upcoming = items
      .filter(item => startOfDay(new Date(item.event_date)) >= today)
      .sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime());

    if (upcoming.length === 0) {
      showToast('No upcoming events on this list', 'info');
      pendingListFlyToRef.current = null;
      return;
    }

    const target = upcoming[0];
    const targetDate = startOfDay(new Date(target.event_date));
    const coords = target.vendor.coordinates; // [lng, lat]

    if (!coords) {
      pendingListFlyToRef.current = null;
      return;
    }

    // Update date strip to match target date
    handleMapDateChange(targetDate);

    // Fly the map
    mapRef.current.easeTo({
      center: [coords[0], coords[1]],
      zoom: 14,
      duration: 1200,
      padding: { top: 100, bottom: 100, left: 50, right: 50 },
    });

    pendingListFlyToRef.current = null;
  }, [isInitialLoad, vendors, handleMapDateChange, showToast]);

  const handleMapBoundsChange = useCallback((bounds: MapBounds) => {
    logger.log('Viewport changed:', bounds);
    const isDisambiguationZoom = mapRef.current && shouldIgnoreBoundsChange(mapRef.current);
    if (isDisambiguationZoom) {
      logger.debug('Skipping all bounds updates during disambiguation zoom');
      return;
    }
    setMapBounds(bounds);
    lastMapBoundsRef.current = bounds;
  }, []);

  const handleSignUpModalRequest = useCallback((headerText: string) => {
    setSignUpModalState({ isOpen: true, headerText });
  }, []);

  const handleCloseSignUpModal = useCallback(() => {
    setSignUpModalState({ isOpen: false, headerText: '' });
  }, []);

  const handleMapLongPress = useCallback(({ lng, lat }: { lng: number; lat: number }) => {
    setEventSubmitState({ isOpen: true, coordinates: [lng, lat] });
  }, []);

  // Guard: ignore vendor marker taps while event placement modal is open
  const handleMarkerClick = useCallback(
    (vendorId: number, displayId?: string, forceSelect?: boolean) => {
      if (eventSubmitState.isOpen) return;
      handleVendorSelect(vendorId, displayId, forceSelect);
    },
    [eventSubmitState.isOpen, handleVendorSelect]
  );

  const handlePreviewMarkerDrag = useCallback((coords: [number, number]) => {
    setEventSubmitState(prev => ({ ...prev, coordinates: coords }));
  }, []);

  const handleDiscardEvent = useCallback(() => {
    setIsDiscardingPreview(true);
    setTimeout(() => {
      setIsDiscardingPreview(false);
      setEventSubmitState({ isOpen: false, coordinates: null });
      setEditingEvent(null);
    }, 330);
  }, []);

  const handleEventSubmitSuccess = useCallback(
    (newEvent: UserEvent, selectedListId?: number | null) => {
      const isEdit = editingEvent !== null;

      setUserEvents(prev => {
        const exists = prev.some(e => e.id === newEvent.id);
        return exists ? prev.map(e => (e.id === newEvent.id ? newEvent : e)) : [newEvent, ...prev];
      });
      setEditingEvent(null);

      // If user associated a list, pin the event to it using start_date
      const pinListId = selectedListId;
      const pinDate = newEvent.start_date;
      if (pinListId && pinDate) {
        addItemToList(pinListId, { vendor_id: newEvent.id, event_date: pinDate, comment: '' })
          .then(newItem => {
            setListItemsCache(prev => ({
              ...prev,
              [pinListId]: [
                {
                  id: newItem.id,
                  vendor_id: newEvent.id,
                  event_date: pinDate,
                  vendor_name: newEvent.name,
                  vendor_category: newEvent.category,
                  vendor_expiry: newEvent.expiry,
                  vendor_schedule_start_time: newEvent.schedule.start_time,
                  vendor_schedule_end_time: newEvent.schedule.end_time,
                  vendor_schedule: newEvent.schedule ?? null,
                },
                ...(prev[pinListId] ?? []),
              ],
            }));
          })
          .catch(() => showToast('Event saved but could not add to list', 'error'));
      }

      // Trigger preview marker pop animation, then close modal and show confirmation
      setIsDiscardingPreview(true);
      setTimeout(() => {
        setIsDiscardingPreview(false);
        setEventSubmitState({ isOpen: false, coordinates: null });
        if (isEdit) {
          showToast('Event updated', 'success');
        } else {
          showToast('Event added to your map', 'success');
          bottomSheetRef.current?.switchToTab('myevents');
          bottomSheetRef.current?.openToLevel(1);
        }
        // Re-fetch vendors to show the new event as an indigo marker immediately.
        // bustCache ensures we bypass any fresh cache entry for the current bounds.
        fetchVendors({ force: true, immediate: true, bustCache: true });
      }, 330);
    },
    [showToast, editingEvent, fetchVendors]
  );

  // Handler for sharing an event
  const handleShareEvent = useCallback(
    async (vendorId: number, date: Date, coordinates: [number, number]) => {
      const url = generateShareUrl(vendorId, date, coordinates);
      try {
        if (navigator.share) {
          await navigator.share({ title: 'ceen', url });
        } else {
          const success = await copyToClipboard(url);
          if (success) {
            showToast('Link copied!', 'success');
          } else {
            showToast('Failed to copy link', 'error');
          }
        }
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          const success = await copyToClipboard(url);
          if (success) {
            showToast('Link copied!', 'success');
          } else {
            showToast('Failed to copy link', 'error');
          }
        }
      }
    },
    [showToast]
  );

  const handleShareList = useCallback(
    async (listUuid: string) => {
      const url = generateListShareUrl(listUuid);

      try {
        if (navigator.share) {
          await navigator.share({
            title: 'ceen',
            url,
          });
        } else {
          const success = await copyToClipboard(url);
          if (success) {
            showToast('Link copied!', 'success');
          } else {
            showToast('Failed to copy link', 'error');
          }
        }
      } catch (err: any) {
        // navigator.share throws AbortError if user dismisses — not a real error
        if (err?.name !== 'AbortError') {
          const success = await copyToClipboard(url);
          if (success) {
            showToast('Link copied!', 'success');
          } else {
            showToast('Failed to copy link', 'error');
          }
        }
      }
    },
    [showToast]
  );

  // Handler for exiting share view and resetting to user's view
  const handleExitShareView = useCallback(() => {
    logger.log('Resetting to default view from share mode');

    // Clear share view state
    setShareViewState(null);

    // Reset to today with default range
    const today = startOfDay(new Date());
    setMapDateFilter(today);

    // Reset API date range to normal (today + MAX_API_DAYS_RANGE)
    setApiDateRange({
      startDate: today,
      endDate: addDays(today, APP_CONFIG.MAX_API_DAYS_RANGE),
    });

    // Fly map to city
    if (mapRef.current) {
      mapRef.current.easeTo({
        zoom: DEFAULT_ZOOM,
        duration: 1000,
      });
    }

    // Handle location-based behavior
    if (locationState === 'granted') {
      // User already granted - fly to their location and fetch data
      logger.log('Location already granted, triggering refresh at user location');
      fetchVendors({ force: true, immediate: true });
    } else if (locationState === 'denied' || locationState === 'unavailable') {
      // User denied or unavailable - stay at current position, just fetch new data
      logger.log('Location denied/unavailable, fetching data for current viewport');
      setLocationState(locationState);
      // Force fetch without clearing existing vendors
      fetchVendors({ force: true, immediate: true });
    } else {
      // Location permission never asked (pending) - trigger permission request
      logger.log('Location permission pending, triggering geolocation');
      setLocationState('pending');

      // Trigger the map's geolocation control
      if (mapRef.current) {
        window.dispatchEvent(new CustomEvent('triggerGeolocation'));
      }
    }
  }, [locationState, fetchVendors, showToast, mapRef]);

  useEffect(() => {
    // Detect mobile devices on component mount
    setIsMobile(isMobileDevice());

    // Update on resize
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Allow vendor data to flow to map/panel on first render
  useEffect(() => {
    setHasValidData(true);
  }, []);

  // Set up periodic updates for GPS vendors - modified to use stable deps and fixed interval

  useEffect(() => {
    // Only set up interval if there are GPS vendors
    const gpsVendors = vendors.filter(vendor => vendor.uses_gps);
    logger.log(`Found ${gpsVendors.length} total GPS vendors for interval setup`);

    if (gpsVendors.length === 0) {
      logger.log('No GPS vendors found, not setting up interval');
      return;
    }

    // Initial update
    logger.log('Running initial GPS update');
    updateGPSLocations();

    // Set up interval with increased time (2 minutes)
    const intervalId = setInterval(() => {
      updateGPSLocations();
    }, APP_CONFIG.GPS_UPDATE_INTERVAL); // 2 minutes in milliseconds

    // Cleanup function
    return () => {
      logger.debug('Cleaning up GPS update interval');
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateGPSLocations]); // Only depend on updateGPSLocations, not vendors

  const handleLogoClick = () => {
    // setIsModalOpen(true);
  };

  const handleLocationPermissionChange = useCallback(
    (
      state: 'pending' | 'granted' | 'denied' | 'unavailable',
      location?: { lat: number; lng: number } | null
    ) => {
      setLocationState(state);
      if (location) {
        // Check if user is outside selected city bounds
        if (selectedCity && state === 'granted') {
          const { bounds } = selectedCity;
          const outsideCity =
            location.lat < bounds.south ||
            location.lat > bounds.north ||
            location.lng < bounds.west ||
            location.lng > bounds.east;
          if (outsideCity) {
            showToast(`Enjoy exploring ${selectedCity.name}!`, 'success');
          }
        }
      }
    },
    [selectedCity, showToast]
  );

  const togglePreset = useCallback(
    (id: string) => {
      setFilterPresets(prev => prev.map(p => (p.id === id ? { ...p, active: !p.active } : p)));
      resetSelection();
    },
    [resetSelection]
  );

  const handleAddList = useCallback((list: CustomList) => {
    setFilterPresets(prev => [
      {
        id: `list-${list.uuid}`,
        type: 'list' as const,
        value: list.uuid,
        label: list.title,
        active: true,
      },
      ...prev.map(p => ({ ...p, active: false })), // reset all active states
    ]);
  }, []);

  const handleAddCuratedList = useCallback(
    async (meta: CuratedListMeta) => {
      try {
        const fullList = await getSharedList(meta.uuid);
        if (fullList) {
          handleAddList(fullList);
          const vendorIds = fullList.items?.map(item => item.vendor.id) || [];
          setActiveListVendorIds(vendorIds);
        }
      } catch (error) {
        logger.error('Error fetching curated list:', error);
      }
    },
    [handleAddList]
  );

  const handleRemovePreset = useCallback((listUuid: string) => {
    setFilterPresets(prev => prev.filter(p => !(p.type === 'list' && p.value === listUuid)));
    setActiveListVendorIds([]);
    clearListParamIfMatches(listUuid);
  }, []);

  // Centralized pin handler — uses ref so concurrent pins share the same list
  // Returns the new ListItem ID on success, null on failure
  const handlePinEvent = useCallback(
    async (
      vendorId: number,
      eventDate: string,
      vendorName: string,
      vendorCategory: string
    ): Promise<number | null> => {
      try {
        // Get or create the active list (ref prevents duplicate creation from race conditions)
        let listId = activeListIdRef.current;
        if (!listId) {
          if (!listCreationPromiseRef.current) {
            listCreationPromiseRef.current = createList({ title: 'my list' })
              .then(list => {
                activeListIdRef.current = list.id;
                setActiveListId(list.id);
                setUserLists([list]);
                listCreationPromiseRef.current = null;
                return list.id;
              })
              .catch(err => {
                listCreationPromiseRef.current = null; // clear so next pin can retry
                throw err;
              });
          }
          listId = await listCreationPromiseRef.current;
        }

        const newItem = await addItemToList(listId, {
          vendor_id: vendorId,
          event_date: eventDate,
          comment: '',
        });
        const resolvedListId = listId as number;
        setListItemsCache(prev => ({
          ...prev,
          [resolvedListId]: [
            ...(prev[resolvedListId] ?? []),
            {
              id: newItem.id,
              vendor_id: vendorId,
              event_date: eventDate,
              vendor_name: vendorName,
              vendor_category: vendorCategory,
              // Expiry/schedule not available at pin time; populated on next list expand
              vendor_expiry: null,
              vendor_schedule_start_time: '',
              vendor_schedule_end_time: '',
              vendor_schedule: null,
            },
          ],
        }));
        setSavedIndicatorListId(resolvedListId);
        setTimeout(
          () => setSavedIndicatorListId(prev => (prev === resolvedListId ? null : prev)),
          2000
        );
        return newItem.id;
      } catch (err) {
        logger.error('Error pinning event:', err);
        return null;
      }
    },
    []
  );

  const handleUnpinEvent = useCallback(
    async (itemId: number, listId?: number): Promise<boolean> => {
      const resolvedListId = listId ?? activeListIdRef.current;
      if (!resolvedListId) return false;
      try {
        await removeItemFromList(resolvedListId, itemId);
        setListItemsCache(prev => ({
          ...prev,
          [resolvedListId]: (prev[resolvedListId] ?? []).filter(i => i.id !== itemId),
        }));
        setSavedIndicatorListId(resolvedListId);
        setTimeout(
          () => setSavedIndicatorListId(prev => (prev === resolvedListId ? null : prev)),
          2000
        );
        return true;
      } catch (err) {
        logger.error('Error unpinning event:', err);
        return false;
      }
    },
    []
  );

  // Called from BottomSheet list view — passes the item's own listId, not the active list
  const handleRemoveItemFromSheet = useCallback(
    async (itemId: number, listId: number) => {
      const success = await handleUnpinEvent(itemId, listId);
      if (!success) showToast('could not remove — try again', 'error', 3000);
    },
    [handleUnpinEvent, showToast]
  );

  const handleMoveItem = useCallback(
    async (
      itemId: number,
      sourceListId: number,
      targetListId: number,
      item: ActiveListItem
    ): Promise<void> => {
      const sourceSnapshot = listItemsCache[sourceListId] ?? [];
      const targetSnapshot = listItemsCache[targetListId] ?? [];

      // Optimistic update
      setListItemsCache(prev => ({
        ...prev,
        [sourceListId]: (prev[sourceListId] ?? []).filter(i => i.id !== itemId),
        [targetListId]: [...(prev[targetListId] ?? []), { ...item }],
      }));

      try {
        await removeItemFromList(sourceListId, itemId);
        const newItem = await addItemToList(targetListId, {
          vendor_id: item.vendor_id,
          event_date: item.event_date,
          comment: '',
        });
        // Replace the optimistic entry with the real backend ID
        setListItemsCache(prev => ({
          ...prev,
          [targetListId]: (prev[targetListId] ?? []).map(i =>
            i.vendor_id === item.vendor_id && i.event_date === item.event_date
              ? { ...i, id: newItem.id }
              : i
          ),
        }));
        setSavedIndicatorListId(targetListId);
        setTimeout(
          () => setSavedIndicatorListId(prev => (prev === targetListId ? null : prev)),
          2000
        );
      } catch {
        // Revert both caches
        setListItemsCache(prev => ({
          ...prev,
          [sourceListId]: sourceSnapshot,
          [targetListId]: targetSnapshot,
        }));
        showToast("couldn't move item", 'error', 3000);
      }
    },
    [listItemsCache, showToast]
  );

  const handleToggleVisibility = useCallback(
    async (listId: number) => {
      const list = userLists.find(l => l.id === listId);
      const current = list?.visibility ?? 'private';
      const next = current === 'public' ? 'private' : 'public';
      setUserLists(prev => prev.map(l => (l.id === listId ? { ...l, visibility: next } : l))); // optimistic
      try {
        await updateList(listId, { visibility: next });
      } catch {
        setUserLists(prev => prev.map(l => (l.id === listId ? { ...l, visibility: current } : l))); // revert
        showToast('could not update visibility — try again', 'error', 3000);
      }
    },
    [userLists, showToast]
  );

  const handleRenameList = useCallback(
    async (listId: number, newTitle: string) => {
      // Gate rename for unverified email accounts — same rule as pinning
      const user = auth.currentUser;
      if (user && !user.emailVerified && user.providerData[0]?.providerId === 'password') {
        showToast(
          'verify your email to save your list — open the console to resend the link',
          'warning',
          5000
        );
        return;
      }
      const list = userLists.find(l => l.id === listId);
      const previousTitle = list?.title ?? 'my list';
      setUserLists(prev => prev.map(l => (l.id === listId ? { ...l, title: newTitle } : l))); // optimistic
      try {
        await updateList(listId, { title: newTitle });
        setSavedIndicatorListId(listId);
        setTimeout(() => setSavedIndicatorListId(prev => (prev === listId ? null : prev)), 2000);
      } catch {
        setUserLists(prev => prev.map(l => (l.id === listId ? { ...l, title: previousTitle } : l))); // revert
        showToast('could not rename — try again', 'error', 3000);
      }
    },
    [userLists, showToast]
  );

  const handleAddUserList = useCallback(
    async (list: CustomList) => {
      setFilterPresets(prev => [
        {
          id: `list-${list.uuid}`,
          type: 'list' as const,
          value: list.uuid,
          label: list.title,
          active: true,
        },
        ...prev.map(p => ({ ...p, active: false })),
      ]);
      let items = listItemsCache[list.id];
      if (!items) {
        try {
          const detail = await getList(list.id);
          items = (detail.items ?? []).map(item => ({
            id: item.id,
            vendor_id: item.vendor.id,
            event_date: item.event_date,
            vendor_name: item.vendor.name,
            vendor_category: item.vendor.category,
            vendor_expiry: item.vendor.expiry,
            vendor_schedule_start_time: item.vendor.schedule?.start_time ?? '',
            vendor_schedule_end_time: item.vendor.schedule?.end_time ?? '',
            vendor_schedule: item.vendor.schedule ?? null,
          }));
          setListItemsCache(prev => ({ ...prev, [list.id]: items! }));
        } catch {
          items = [];
        }
      }
      setActiveListVendorIds(items.map(i => i.vendor_id));
    },
    [listItemsCache]
  );

  const handleExpandList = useCallback(
    async (id: number) => {
      if (listItemsCache[id]) return; // already cached
      try {
        const detail = await getList(id);
        const items: ActiveListItem[] = (detail.items ?? []).map(item => ({
          id: item.id,
          vendor_id: item.vendor.id,
          event_date: item.event_date,
          vendor_name: item.vendor.name,
          vendor_category: item.vendor.category,
          vendor_expiry: item.vendor.expiry,
          vendor_schedule_start_time: item.vendor.schedule?.start_time ?? '',
          vendor_schedule_end_time: item.vendor.schedule?.end_time ?? '',
          vendor_schedule: item.vendor.schedule ?? null,
        }));
        setListItemsCache(prev => ({ ...prev, [id]: items }));
      } catch (err) {
        logger.error('Error expanding list:', err);
      }
    },
    [listItemsCache]
  );

  const handleExpandCuratedList = useCallback(
    async (uuid: string) => {
      if (curatedItemsCache[uuid]) return; // already cached
      try {
        const list = await getSharedList(uuid);
        if (list.items) {
          const items: ActiveListItem[] = list.items.map(item => ({
            id: item.id,
            vendor_id: item.vendor.id,
            event_date: item.event_date,
            vendor_name: item.vendor.name,
            vendor_category: item.vendor.category,
            vendor_expiry: item.vendor.expiry,
            vendor_schedule_start_time: item.vendor.schedule?.start_time ?? '',
            vendor_schedule_end_time: item.vendor.schedule?.end_time ?? '',
            vendor_schedule: item.vendor.schedule ?? null,
          }));
          setCuratedItemsCache(prev => ({ ...prev, [uuid]: items }));
        }
      } catch (err) {
        logger.error('Error expanding curated list:', err);
      }
    },
    [curatedItemsCache]
  );

  const handleCreateList = useCallback(async (): Promise<number> => {
    if (!auth.currentUser) {
      setCityDrawerState({ open: true, contextText: 'sign in to save lists' });
      throw new Error('unauthenticated');
    }
    try {
      const newList = await createList({ title: 'new list' });
      setUserLists(prev => [newList, ...prev]);
      setListItemsCache(prev => ({ ...prev, [newList.id]: [] }));
      activeListIdRef.current = newList.id;
      setActiveListId(newList.id);
      return newList.id;
    } catch (err) {
      logger.error('Error creating list:', err);
      showToast('could not create list — try again', 'error', 3000);
      throw err;
    }
  }, [showToast]);

  const handleDeleteList = useCallback(
    async (listId: number) => {
      const list = userLists.find(l => l.id === listId);
      if (!list) return;
      const cachedItems = listItemsCache[listId];

      // Remove from state immediately (optimistic)
      setUserLists(prev => prev.filter(l => l.id !== listId));
      setListItemsCache(prev => {
        const next = { ...prev };
        delete next[listId];
        return next;
      });
      setFilterPresets(prev => prev.filter(p => !(p.type === 'list' && p.value === list.uuid)));
      setActiveListVendorIds([]);

      // If this was active, switch to first remaining list
      if (activeListIdRef.current === listId) {
        const remaining = userLists.filter(l => l.id !== listId);
        if (remaining.length > 0) {
          activeListIdRef.current = remaining[0].id;
          setActiveListId(remaining[0].id);
        } else {
          activeListIdRef.current = null;
          setActiveListId(null);
        }
      }

      try {
        await deleteList(listId);
        showToast('list deleted', 'info', 2000);
      } catch {
        // Restore on API failure
        setUserLists(prev => [...prev, list]);
        if (cachedItems) setListItemsCache(prev => ({ ...prev, [listId]: cachedItems }));
        showToast('could not delete list — try again', 'error', 3000);
      }
    },
    [userLists, listItemsCache, showToast]
  );

  const handleSetActiveList = useCallback(
    async (listId: number) => {
      activeListIdRef.current = listId;
      setActiveListId(listId);
      // Load items if not yet cached
      if (!listItemsCache[listId]) {
        try {
          const detail = await getList(listId);
          const items: ActiveListItem[] = (detail.items ?? []).map(item => ({
            id: item.id,
            vendor_id: item.vendor.id,
            event_date: item.event_date,
            vendor_name: item.vendor.name,
            vendor_category: item.vendor.category,
            vendor_expiry: item.vendor.expiry,
            vendor_schedule_start_time: item.vendor.schedule?.start_time ?? '',
            vendor_schedule_end_time: item.vendor.schedule?.end_time ?? '',
            vendor_schedule: item.vendor.schedule ?? null,
          }));
          setListItemsCache(prev => ({ ...prev, [listId]: items }));
        } catch (err) {
          logger.error('Error loading items for active list:', err);
        }
      }
    },
    [listItemsCache]
  );

  // Apply client-side filtering based on active presets
  const filteredVendors = useMemo(() => {
    const hasActiveCategories = activeCategories.length > 0;
    const hasActiveList = activeListUuid !== null;

    if (!hasActiveCategories && !hasActiveList) return vendors;

    if (hasActiveList) {
      const listVendors = vendors.filter(v => activeListVendorIds.includes(v.id));

      if (hasActiveCategories) {
        const otherVendors = vendors.filter(
          v => !activeListVendorIds.includes(v.id) && activeCategories.includes(v.category)
        );
        return [...listVendors, ...otherVendors];
      }

      return listVendors;
    }

    return vendors.filter(v => activeCategories.includes(v.category));
  }, [vendors, activeCategories, activeListUuid, activeListVendorIds]);

  // Calculate active vendor IDs for map markers - ensures only vendors passing filters are considered active
  const activeVendorIds = useMemo((): Set<number> | null => {
    const hasActiveCategories = activeCategories.length > 0;
    const hasActiveList = activeListUuid !== null;

    if (!hasActiveCategories && !hasActiveList) return null;

    return new Set(filteredVendors.map(v => v.id));
  }, [filteredVendors, activeCategories, activeListUuid]);

  // Create a map-specific list of vendors that ensures only one marker per vendor
  // regardless of how many days they span
  const mapVendors = useMemo(() => {
    logger.debug('🔄 mapVendors recalculating due to deps change');
    logger.debug('🔍 Filtering vendors for date:', mapDateFilter, mapDateFilter.getDay());
    logger.debug('🔍 Total vendors before filter:', filteredVendors.length);
    const dateFilteredVendors = filterVendorsByDate(filteredVendors, mapDateFilter);
    const deduplicated = deduplicateVendors(dateFilteredVendors);
    return deduplicated;
  }, [filteredVendors, mapDateFilter]);

  // All vendors for the current date — passed to map so all markers render.
  // Category/list filtering is expressed via activeVendorIds (dimming) not by
  // removing markers. Date filter still hides markers completely.
  const allMapVendors = useMemo(() => {
    const dateFilteredVendors = filterVendorsByDate(vendors, mapDateFilter);
    return deduplicateVendors(dateFilteredVendors);
  }, [vendors, mapDateFilter]);

  // Calculate available dates for the date swipe indicator
  const availableDates = useMemo(() => {
    const dates = getDatesWithVendors(filteredVendors, apiDateRange);
    logger.debug('📅 Available dates recalculated:', dates.length);
    return dates;
  }, [filteredVendors, apiDateRange]);

  // Preserve or adjust date selection when available dates change
  useEffect(() => {
    logger.log('🔍 availableDates changed:', {
      count: availableDates.length,
      currentMapDateFilter: mapDateFilter,
      dates: availableDates.map(d => d.dateStr),
    });

    if (availableDates.length === 0) return;

    // Check if current mapDateFilter exists in the new availableDates
    const currentDateExists = availableDates.some(dateInfo =>
      isSameDay(dateInfo.date, mapDateFilter)
    );

    if (currentDateExists) {
      // Current date still valid - keep it
      return;
    }

    // Current date not in new data - pick closest available date
    const sortedByDistance = [...availableDates].sort((a, b) => {
      const distA = Math.abs(a.date.getTime() - mapDateFilter.getTime());
      const distB = Math.abs(b.date.getTime() - mapDateFilter.getTime());
      return distA - distB;
    });

    if (sortedByDistance.length > 0) {
      handleMapDateChange(sortedByDistance[0].date);
    }
  }, [availableDates]);

  // Parse share link from URL on mount
  useEffect(() => {
    if (hasShareLinkParams()) {
      const { eventId, date } = parseShareLinkFromUrl();

      if (eventId && date) {
        logger.log('Deep link detected:', { eventId, date });
        setPendingDeepLink({ eventId, date });
        // Clear the URL params so refresh doesn't re-trigger
        clearShareLinkParams();
      }
    }
  }, []); // Only run once on mount

  // Process pending deep link once vendors are loaded
  useEffect(() => {
    if (!pendingDeepLink || isInitialLoad || vendors.length === 0) {
      return;
    }

    const { eventId, date } = pendingDeepLink;

    // Find the vendor
    const vendor = vendors.find(v => v.id === eventId);

    if (!vendor) {
      // Event not found
      showToast('This event is no longer available', 'info');
      setPendingDeepLink(null);
      return;
    }

    // Check if the date is within our available range
    const today = INITIAL_DATE;
    const maxDate = addDays(today, APP_CONFIG.MAX_API_DAYS_RANGE);
    const linkDate = startOfDay(date);

    let targetDate = linkDate;
    let dateMessage = '';

    if (linkDate < today) {
      // Date is in the past
      targetDate = today;
      dateMessage = "This event has passed - showing today's list";
    } else if (linkDate > maxDate) {
      // Date is too far in future
      targetDate = today;
      dateMessage = "This event isn't scheduled yet - showing today's list";
    }

    // Show message if date was adjusted
    if (dateMessage) {
      showToast(dateMessage, 'info');
    }

    // Update the map date filter
    handleMapDateChange(targetDate);

    // Format display ID
    const displayId = `${eventId}-${format(targetDate, 'yyyy-MM-dd')}`;

    // Select the vendor (this should trigger the panel to show it)
    handleVendorSelect(eventId, displayId);

    // Use the panel card click handler to fly to location and open popup
    if (vendor.coordinates) {
      // Small delay to let state updates propagate
      setTimeout(() => {
        if ((window as any).handlePanelCardClick) {
          (window as any).handlePanelCardClick(
            eventId,
            displayId,
            vendor.coordinates,
            true // shouldBeSelected
          );
        }
      }, 100);
    }

    // Clear the pending deep link
    setPendingDeepLink(null);
  }, [pendingDeepLink, isInitialLoad, vendors, handleMapDateChange, handleVendorSelect, showToast]);

  return (
    <AuthProvider>
      {cityDrawerState.open &&
        createPortal(
          <CityPicker
            mode="drawer"
            contextText={cityDrawerState.contextText}
            onClose={() => setCityDrawerState({ open: false })}
            showToast={showToast}
            onRefreshCalendar={() => {
              setCalendarEventsLoading(true);
              setCalendarEventsError(null);
              getCalendarEvents()
                .then(events => {
                  setCalendarEvents(events);
                  setCalendarEventsLoading(false);
                })
                .catch(err => {
                  setCalendarEventsLoading(false);
                  const code = (err as { code?: string })?.code;
                  if (code === 'calendar_token_expired' || code === 'calendar_api_error') {
                    setCalendarEventsError(code);
                  }
                });
            }}
            onCitySelected={city => {
              setSelectedCity(city);
              const bounds = {
                minLng: city.bounds.west,
                minLat: city.bounds.south,
                maxLng: city.bounds.east,
                maxLat: city.bounds.north,
              };
              setMapBounds(bounds);
              setLocationState('denied');
              setShareViewState(null);
              setCityDrawerState({ open: false });

              // Reset to default date range
              const today = startOfDay(new Date());
              setMapDateFilter(today);
              setApiDateRange({
                startDate: today,
                endDate: addDays(today, APP_CONFIG.MAX_API_DAYS_RANGE),
              });

              // Fly map to city
              if (mapRef.current) {
                mapRef.current.easeTo({
                  center: [city.lng, city.lat],
                  zoom: city.zoom ?? DEFAULT_ZOOM,
                  duration: 1000,
                });
              }
            }}
          />,
          document.body
        )}
      <div
        className="App"
        style={{
          height: '100vh',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Blocking mode — first time, no city stored */}
        {!selectedCity && !shareViewState?.active && (
          <CityPicker
            onCitySelected={city => {
              setSelectedCity(city);
              setMapBounds({
                minLng: city.bounds.west,
                minLat: city.bounds.south,
                maxLng: city.bounds.east,
                maxLat: city.bounds.north,
              });
              setLocationState('denied');
            }}
            showToast={showToast}
          />
        )}
        {selectedCity && (
          <>
            {/* Critical change: Move the map container BEFORE the header in DOM order */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
              }}
            >
              <MapComponent
                filteredVendors={hasValidData ? allMapVendors : EMPTY_VENDORS_ARRAY}
                selectedVendorId={selection.vendorId}
                selectedDisplayIds={selection.allDisplayIds}
                onMarkerClick={handleMarkerClick}
                onViewportChange={handleMapBoundsChange}
                onVisibleVendorsChange={handleVisibleVendorsChange}
                isMobile={isMobile}
                currentDate={mapDateFilter}
                locationState={locationState}
                onLocationPermissionChange={handleLocationPermissionChange}
                onSignUpModalRequest={handleSignUpModalRequest}
                onShareEvent={handleShareEvent}
                showToast={showToast}
                onMapRef={handleMapRef}
                onZoomChange={handleZoomChange}
                shareViewState={shareViewState}
                activeVendorIds={activeVendorIds}
                selectedCity={selectedCity}
                activeListItems={activeListItems}
                onPinEvent={handlePinEvent}
                onUnpinEvent={handleUnpinEvent}
                onMapLongPress={handleMapLongPress}
                previewMarkerCoords={eventSubmitState.isOpen ? eventSubmitState.coordinates : null}
                onPreviewMarkerDrag={handlePreviewMarkerDrag}
                previewMarkerPopping={isDiscardingPreview}
                gcalEvents={calendarEvents}
              />
            </div>

            {/* Header portalled to body so it escapes #root's isolation stacking context */}
            {createPortal(
              <div
                className="px-3"
                ref={headerRef}
                style={{
                  position: 'fixed',
                  top: '0',
                  left: '0',
                  right: '0',
                  height: 'var(--header-height)',
                  zIndex: 10003,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                {/* Background overlay container - using consistent styling */}
                <div
                  className="ceen-panel-bg ceen-glass"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: -1,
                  }}
                />
                {/* Logo section */}
                <AppLogo onLogoClick={handleLogoClick} isMobile={isMobile} />
                {/* Drag handle — centered in header, pinned to bottom */}
                <div
                  onClick={() => setCityDrawerState({ open: true })}
                  style={{
                    position: 'absolute',
                    bottom: '6px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '4px 24px',
                    cursor: 'pointer',
                    zIndex: 10000,
                  }}
                >
                  <div
                    style={{
                      width: '36px',
                      height: '4px',
                      borderRadius: '2px',
                      backgroundColor: 'rgba(0,0,0,0.2)',
                    }}
                  />
                </div>

                {/* Auth button on the right */}
                <div
                  style={{
                    flex: '0 0 auto',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                  className={isMobile ? 'mobile-scale' : ''}
                >
                  {/* Exit share view — only shown in share mode */}
                  {shareViewState?.active && (
                    <div style={{ position: 'relative' }}>
                      <ExitShareViewButton
                        onExit={handleExitShareView}
                        isMobile={isMobile}
                        isUnified={false}
                        isAnimatingOut={false}
                      />
                    </div>
                  )}
                  <ConditionalSignUpComponent
                    onOpenConsole={() => setCityDrawerState({ open: true })}
                    pinCount={activeListItems.length}
                    onOpenList={() => bottomSheetRef.current?.openToLevel(2)}
                  />
                </div>
              </div>,
              document.body
            )}

            {/* Confirmation Dialog using SignUpModal component */}
            <SignUpModal
              isOpen={signUpModalState.isOpen}
              onClose={handleCloseSignUpModal}
              headerText={signUpModalState.headerText}
              isMobile={isMobile}
            />

            {/* Toast Notifications */}
            <ToastContainer toasts={toasts} onDismiss={dismissToast} />
            {/* Geolocate Button */}
            <GeolocateButton showToast={showToast} />
            {/* Filter Panel */}
            <BottomSheet
              ref={bottomSheetRef}
              isMobile={isMobile}
              filterPresets={filterPresets}
              onTogglePreset={togglePreset}
              currentDate={mapDateFilter}
              onDateChange={handleMapDateChange}
              availableDates={availableDates}
              curatedLists={curatedLists}
              onAddList={handleAddCuratedList}
              onRemovePreset={handleRemovePreset}
              onShareList={handleShareList}
              shareViewState={shareViewState}
              userLists={userLists}
              listItemsCache={listItemsCache}
              onExpandList={handleExpandList}
              curatedItemsCache={curatedItemsCache}
              onExpandCuratedList={handleExpandCuratedList}
              activeListId={activeListId}
              savedIndicatorListId={savedIndicatorListId}
              onCreateList={handleCreateList}
              onDeleteList={handleDeleteList}
              onSetActiveList={handleSetActiveList}
              onAddUserList={handleAddUserList}
              onRemoveItem={handleRemoveItemFromSheet}
              onMoveItem={handleMoveItem}
              onRenameList={handleRenameList}
              onToggleVisibility={handleToggleVisibility}
              mapVendors={hasValidData ? (mapVendors as ExpandedVendor[]) : EMPTY_EXPANDED_VENDORS}
              isInitialLoad={isInitialLoad}
              isVendorFeedLoading={isLoading}
              selectedVendorId={selection.vendorId}
              selectedDisplayIds={selection.allDisplayIds}
              onVendorSelect={handleVendorSelect}
              activeListItems={activeListItems}
              showToast={showToast}
              onSignUpModalRequest={handleSignUpModalRequest}
              onShareEvent={handleShareEvent}
              onPinEvent={handlePinEvent}
              onUnpinEvent={handleUnpinEvent}
              userEvents={userEvents}
              pinnedEvents={pinnedEvents}
              onEditEvent={event => {
                setEditingEvent(event);
                setEventSubmitState({ isOpen: true, coordinates: event.coordinates });
              }}
              onDeleteUserEvent={async id => {
                try {
                  await deleteUserEvent(id);
                  setUserEvents(prev => prev.filter(e => e.id !== id));
                  setVendorMap(prev => {
                    const next = new Map(prev);
                    for (const [k, v] of next) {
                      if (v.id === id) next.delete(k);
                    }
                    return next;
                  });
                } catch {
                  showToast('Could not delete event', 'error');
                }
              }}
              onSubmitForReview={async id => {
                try {
                  await submitEventForReview(id);
                  setUserEvents(prev =>
                    prev.map(e =>
                      e.id === id ? { ...e, visibility: 'pending_review' as const } : e
                    )
                  );
                  showToast('Submitted for review', 'success');
                } catch {
                  showToast('Could not submit for review', 'error');
                }
              }}
              calendarEvents={calendarEvents}
              calendarEventsLoading={calendarEventsLoading}
              calendarEventsError={calendarEventsError}
              onRefreshEvents={() => {
                getUserEvents()
                  .then(setUserEvents)
                  .catch(() => {});
                getPinnedEvents()
                  .then(setPinnedEvents)
                  .catch(() => {});
                setCalendarEventsLoading(true);
                setCalendarEventsError(null);
                getCalendarEvents()
                  .then(events => {
                    setCalendarEvents(events);
                    setCalendarEventsLoading(false);
                  })
                  .catch(err => {
                    setCalendarEventsLoading(false);
                    const code = (err as { code?: string })?.code;
                    if (code === 'calendar_token_expired' || code === 'calendar_api_error') {
                      setCalendarEventsError(code);
                    }
                  });
              }}
              onCalendarReconnect={() => setCityDrawerState({ open: true })}
            />
            <EventSubmitModal
              isOpen={eventSubmitState.isOpen}
              onClose={() => {
                setEventSubmitState({ isOpen: false, coordinates: null });
                setEditingEvent(null);
              }}
              coordinates={
                editingEvent
                  ? (eventSubmitState.coordinates ?? editingEvent.coordinates)
                  : eventSubmitState.coordinates
              }
              onSuccess={handleEventSubmitSuccess}
              showToast={showToast}
              isMobile={isMobile}
              initialData={editingEvent ?? undefined}
              selectedCityRegion={selectedCity?.id}
              userLists={userLists}
              onAuthRequired={() =>
                setCityDrawerState({
                  open: true,
                  contextText: 'to save your events, login to your account',
                })
              }
              onDiscard={handleDiscardEvent}
            />
          </>
        )}
      </div>
    </AuthProvider>
  );
};

export default App;
